import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { SubmitSongFeedbackRequestDTO } from '@/core/api/dtos/request/playlist/submitSongFeedback'
import { SongFeedbackResponseDTO } from '@/core/api/dtos/response/song/songFeedback'
import { SubmitFeedbackResponseDTO } from '@/core/api/dtos/response/song/submitFeedback'

import apiClient from '@core/api/client'

import { UpdateFavoriteRequestDTO } from '../dtos/request/playlist/favorite'
import { RequestCustomPlaylistRequestDTO } from '../dtos/request/playlist/requestCustom'

export const playlistService = {
	/**
	 * Get all playlists
	 */
	getPlaylists(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/playlists', config)
	},

	/**
	 * Get assigned playlists
	 */
	getAssignedPlaylists(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/playlists/assigned?public=false', config)
	},

	/**
	 * Get in-use playlists
	 */
	getInUsePlaylists(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/playlists/in-use', config)
	},

	/**
	 * Get favorite playlists
	 */
	getFavoritePlaylists(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/playlists/assigned?favorite=true', config)
	},

	/**
	 * Add playlist to favorites
	 */
	addPlaylistToFavorites(
		playlistId: number,
		data: UpdateFavoriteRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/my/playlists/${playlistId}/favorites`, data, config)
	},

	/**
	 * Request a custom playlist
	 */
	requestCustomPlaylist(
		data: RequestCustomPlaylistRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post('/my/playlists/request', data, config)
	},

	/**
	 * Get playlist details by ID
	 */
	getPlaylistById(
		playlistId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/playlists/${playlistId}`, config)
	},

	/**
	 * Get playlist sample (preview songs)
	 */
	getPlaylistSample(
		playlistId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/playlists/${playlistId}/sample`, config)
	},

	/**
	 * Approve a playlist
	 */
	approvePlaylist(
		playlistId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/playlists/${playlistId}/approve`, {}, config)
	},

	/**
	 * Get all feedback for a playlist
	 */
	getPlaylistFeedback(
		playlistId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse<{ data: SongFeedbackResponseDTO[] }>> {
		return apiClient.get(`/my/playlists/${playlistId}/feedback`, config)
	},

	/**
	 * Submit feedback for a song in a playlist
	 */
	submitSongFeedback(
		playlistId: number,
		songId: number,
		data: SubmitSongFeedbackRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse<{ data?: SubmitFeedbackResponseDTO }>> {
		return apiClient.post(
			`/playlists/${playlistId}/songs/${songId}/feedback`,
			data,
			config
		)
	},

	/**
	 * Get groups a playlist is assigned to
	 */
	getPlaylistGroups(
		playlistId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/playlists/${playlistId}/groups`, config)
	},

	/**
	 * Get zones a playlist is assigned to
	 */
	getPlaylistZones(
		playlistId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/playlists/${playlistId}/zones`, config)
	},
}
